import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trophy, Users, Radio, ChevronRight, ArrowLeft, Clock, Award, Swords, Plus, X, Lock, Eye, EyeOff, AlertTriangle, CheckCircle2 } from "lucide-react";
import FollowTeamButton from "@/components/FollowTeamButton";

/**
 * /teams/:teamId — Comprehensive Team Details (single source of truth).
 * Reads /api/public/teams/by-id/:teamId and subscribes to /api/public/events/stream
 * for live set-score updates so scores mutate in real time without a refresh.
 * Powers: PublicHome cards, Standings clicks, Fixtures clicks, Owner Command
 * Center competitor rows — every team icon in the app.
 */
const STAGE_LABEL = {
  group: "Group Stage",
  quarter: "Quarterfinal",
  semi: "Semifinal",
  final: "Grand Final",
};
const STATUS_COLORS = {
  live: "bg-red-500/20 text-red-300 border-red-500/40",
  scheduled: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40",
  finished: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
};

const fmtDate = (iso) => {
  if (!iso) return "TBD";
  const d = new Date(iso);
  return isNaN(d) ? "TBD" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function TeamDetails() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [openTieId, setOpenTieId] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get(`/public/teams/by-id/${teamId}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Team not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [teamId]);

  // Real-time SSE — refresh on set_scored / finalized so this team's live scores mutate live.
  useEffect(() => {
    if (!teamId) return;
    const base = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
    const es = new EventSource(`${base}/api/public/events/stream`);
    const refresh = () => load();
    es.addEventListener("match.set_scored", refresh);
    es.addEventListener("match.finalized", refresh);
    es.onerror = () => { /* silent — auto-reconnect */ };
    return () => es.close();
    // eslint-disable-next-line
  }, [teamId]);

  const filteredTies = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.ties;
    if (["live", "scheduled", "finished"].includes(filter)) return data.ties.filter((t) => t.status === filter);
    return data.ties.filter((t) => t.stage === filter);
  }, [data, filter]);

  const liveTies = useMemo(() => (data?.ties || []).filter((t) => t.status === "live"), [data]);
  const upcomingTies = useMemo(() => (data?.ties || []).filter((t) => t.status === "scheduled").slice(0, 5), [data]);

  if (loading) return <div className="p-10 text-zinc-400" data-testid="team-details-loading">Loading team profile…</div>;
  if (err) return <div className="p-10 text-red-400" data-testid="team-details-error">{err}</div>;
  if (!data) return null;

  const { team, players, standings } = data;
  const h2h = data.next_opponent_h2h;

  return (
    <div className="min-h-screen bg-[#060d09] text-white pb-16" data-testid={`team-details-${team.id}`}>
      {/* Hero */}
      <div
        className="relative border-b border-white/10 p-6 md:p-10"
        style={{ background: `linear-gradient(135deg, ${team.brand_color || "#00A86B"}22 0%, #0a1611 60%)` }}
      >
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white mb-4"
          data-testid="team-details-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="flex items-start gap-4 md:gap-6 flex-wrap">
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-black/40 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
            {team.logo_url
              ? <img src={team.logo_url} alt={team.name} className="w-full h-full object-contain" />
              : <Trophy className="w-10 h-10 text-zinc-500" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Mara Padel League · Team Profile</div>
            <h1 className="font-anton text-4xl md:text-6xl uppercase tracking-tighter leading-none mt-1" data-testid="team-details-name">
              {team.name}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-400">
              {team.group && <Badge className="bg-[#F4B400] text-black uppercase">Group {team.group}</Badge>}
              {team.owner_name && <span>Owner · <b className="text-white">{team.owner_name}</b></span>}
              {typeof team.seed_number === "number" && <span>Seed <b className="text-white">#{team.seed_number}</b></span>}
              <div className="ml-auto">
                <FollowTeamButton teamId={team.id} teamName={team.name} />
              </div>
            </div>
            {team.description && <p className="mt-3 text-sm text-zinc-300 max-w-2xl">{team.description}</p>}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-2">
          <StatChip label="Position" value={standings.position ? `#${standings.position}` : "—"} tone={standings.position === 1 ? "gold" : standings.position === 2 ? "green" : "neutral"} />
          <StatChip label="Ties Played" value={standings.ties_played} />
          <StatChip label="Games Won" value={standings.games_won} tone="green" />
          <StatChip label="Games Lost" value={standings.games_lost} tone="red" />
          <StatChip label="Game Diff" value={`${standings.game_diff > 0 ? "+" : ""}${standings.game_diff}`} tone={standings.game_diff > 0 ? "green" : standings.game_diff < 0 ? "red" : "neutral"} />
          <StatChip label="Avg GD" value={`${standings.avg_game_diff > 0 ? "+" : ""}${standings.avg_game_diff}`} />
        </div>

        {standings.qualification_status && (
          <div className="mt-3 text-xs" data-testid="team-details-qualification">
            Status:{" "}
            <b className={
              standings.qualification_status === "Auto-Qualified" ? "text-emerald-400" :
              standings.qualification_status === "Wild Card Contender" ? "text-[#F4B400]" :
              "text-zinc-300"
            }>
              {standings.qualification_status}
            </b>
          </div>
        )}
      </div>

      <div className="p-6 md:p-10 grid gap-6 lg:grid-cols-3">
        {/* Squad + Live + Upcoming */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="p-5 bg-[#0f1a15] border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#F4B400]" />
              <h2 className="font-anton text-xl uppercase tracking-tight">Squad</h2>
              <span className="text-xs text-zinc-500 ml-auto">{players.length} players</span>
            </div>
            <SquadBySeedPair players={players} team={team} ties={data?.ties || []} onLineupSaved={load} />
          </Card>

          {liveTies.length > 0 && (
            <Card className="p-5 bg-gradient-to-b from-[#1a0808] to-[#0f1a15] border-red-500/40" data-testid="team-details-live">
              <div className="flex items-center gap-2 mb-3">
                <Radio className="w-4 h-4 text-red-400 animate-pulse" />
                <h2 className="font-anton text-xl uppercase tracking-tight text-red-300">Live Now</h2>
              </div>
              <div className="space-y-2">
                {liveTies.map((t) => (
                  <TieRow key={t.tie_id} tie={t} teamId={team.id} onOpen={() => setOpenTieId(t.tie_id)} />
                ))}
              </div>
            </Card>
          )}

          {upcomingTies.length > 0 && (
            <Card className="p-5 bg-[#0f1a15] border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-[#F4B400]" />
                <h2 className="font-anton text-xl uppercase tracking-tight">Upcoming</h2>
              </div>
              <div className="space-y-2">
                {upcomingTies.map((t) => (
                  <TieRow key={t.tie_id} tie={t} teamId={team.id} onOpen={() => setOpenTieId(t.tie_id)} />
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="lg:col-span-2">
          {h2h && (
            <Card className="p-4 bg-gradient-to-r from-[#1a1408] via-[#0f1a15] to-[#0f1a15] border-[#F4B400]/30 mb-4" data-testid="h2h-strip">
              <div className="flex items-center gap-2 mb-2">
                <Swords className="w-4 h-4 text-[#F4B400]" />
                <div className="font-anton text-lg uppercase tracking-tight text-white">Next Up · Head to Head</div>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-auto">{fmtDate(h2h.scheduled_at)}</span>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Vs</div>
                  <Link to={`/teams/${h2h.opponent.id}`} className="font-anton text-2xl uppercase text-white hover:text-[#F4B400]" data-testid="h2h-opponent-link">
                    {h2h.opponent.name || "TBD"}
                  </Link>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <Mini label="Prior" value={h2h.meetings} />
                  <Mini label="Wins" value={h2h.wins} tone="green" />
                  <Mini label="Losses" value={h2h.losses} tone="red" />
                  <Mini label="Games" value={`${h2h.games_won}–${h2h.games_lost}`} />
                </div>
              </div>
            </Card>
          )}
          <Card className="p-5 bg-[#0f1a15] border-white/10">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Award className="w-4 h-4 text-[#F4B400]" />
              <h2 className="font-anton text-xl uppercase tracking-tight">Match History</h2>
              <span className="text-xs text-zinc-500 ml-auto">{filteredTies.length} tie{filteredTies.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="flex flex-wrap gap-1 mb-4" data-testid="team-details-filters">
              {[
                ["all", "All"], ["live", "Live"], ["scheduled", "Upcoming"], ["finished", "Completed"],
                ["group", "League"], ["quarter", "QF"], ["semi", "SF"], ["final", "Final"],
              ].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  data-testid={`team-details-filter-${k}`}
                  className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded border ${
                    filter === k
                      ? "bg-[#00A86B]/20 border-[#00A86B]/60 text-[#00A86B]"
                      : "bg-black/30 border-white/10 text-zinc-400 hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {filteredTies.length === 0 ? (
              <div className="text-sm text-zinc-500 italic py-6 text-center">No ties match this filter.</div>
            ) : (
              <div className="space-y-2">
                {filteredTies.map((t) => (
                  <TieDetailCard
                    key={t.tie_id}
                    tie={t}
                    teamId={team.id}
                    expanded={openTieId === t.tie_id}
                    onToggle={() => setOpenTieId(openTieId === t.tie_id ? null : t.tie_id)}
                  />
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function StatChip({ label, value, tone = "neutral" }) {
  const colors = {
    gold: "text-[#F4B400] border-[#F4B400]/30 bg-[#F4B400]/5",
    green: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
    red: "text-red-400 border-red-500/30 bg-red-500/5",
    neutral: "text-white border-white/10 bg-black/30",
  };
  return (
    <div className={`p-2 rounded-lg border ${colors[tone]}`}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-anton text-2xl leading-none mt-1">{value}</div>
    </div>
  );
}

function Mini({ label, value, tone = "neutral" }) {
  const colors = {
    green: "text-emerald-400",
    red: "text-red-400",
    neutral: "text-white",
  };
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-anton text-xl leading-none mt-0.5 ${colors[tone]}`}>{value}</div>
    </div>
  );
}

function SquadBySeedPair({ players, team, ties, onLineupSaved }) {
  const { user } = useAuth();
  const isMyTeam = user?.role === "owner" && user?.team_id === team?.id;

  // Next upcoming LEAGUE tie for THIS team — that's the lineup to assign.
  const nextTie = useMemo(() => {
    return (ties || [])
      .filter((t) => (t.status || "").toLowerCase() === "scheduled" && (t.matches?.[0]?.stage || "group") === "group")
      .sort((a, b) => {
        const at = a.matches?.[0]?.scheduled_at || "";
        const bt = b.matches?.[0]?.scheduled_at || "";
        return at.localeCompare(bt);
      })[0];
  }, [ties]);

  if (isMyTeam && nextTie) {
    return <InlineLineupEditor team={team} players={players} tie={nextTie} onSaved={onLineupSaved} />;
  }

  // Read-only roster view for everyone else (spectators + non-owners).
  return <SquadRosterView players={players} />;
}

function SquadRosterView({ players }) {
  const bySeed = useMemo(() => {
    const buckets = { 1: [], 2: [], 3: [], other: [] };
    players.forEach((p) => {
      const s = parseInt(p.seed_level);
      if (s === 1 || s === 2 || s === 3) buckets[s].push(p);
      else buckets.other.push(p);
    });
    return buckets;
  }, [players]);

  if (players.length === 0) return <div className="text-xs text-zinc-500 italic py-4">No players on the roster.</div>;

  const seedColors = { 1: "#F4B400", 2: "#00A86B", 3: "#7DD3FC" };
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((s) => (
        <div key={s}>
          <div className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: seedColors[s] }}>
            Seed {s} Pair · {bySeed[s].length} player{bySeed[s].length !== 1 ? "s" : ""}
          </div>
          <div className="space-y-1">
            {bySeed[s].length === 0
              ? <div className="text-[11px] text-zinc-600 italic px-2">Unassigned</div>
              : bySeed[s].map((p) => <PlayerRow key={p.id} player={p} />)}
          </div>
        </div>
      ))}
      {bySeed.other.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest mb-1.5 text-zinc-500">Other</div>
          <div className="space-y-1">
            {bySeed.other.map((p) => <PlayerRow key={p.id} player={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function InlineLineupEditor({ team, players, tie, onSaved }) {
  const tieId = tie.tie_id;
  const [payload, setPayload] = useState(null);
  // Flat 6-slot array: [Pair A, Pair B, Pair C]. No seed labels.
  const [pairs, setPairs] = useState([[], [], []]);
  const [busy, setBusy] = useState(false);

  const CATEGORY_POINTS = { "A+": 1, A: 2, B: 3, C: 4, D: 5, E: 6 };
  const pts = (p) =>
    CATEGORY_POINTS[(p?.category || p?.letter_category || "").toUpperCase()] ?? 6;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lineups/mine/${tieId}`);
        setPayload(data);
        if (data.my_lineup) {
          setPairs([
            (data.my_lineup.seed_1 || []).map((p) => p.id),
            (data.my_lineup.seed_2 || []).map((p) => p.id),
            (data.my_lineup.seed_3 || []).map((p) => p.id),
          ]);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load lineup");
      }
    })();
  }, [tieId]);

  const selected = useMemo(() => new Set(pairs.flat()), [pairs]);
  const totalSelected = pairs.flat().length;

  const addPlayer = (playerId) => {
    setPairs((prev) => {
      const next = prev.map((p) => [...p]);
      if (next.flat().includes(playerId)) return prev;
      for (let i = 0; i < 3; i++) {
        if (next[i].length < 2) {
          next[i].push(playerId);
          return next;
        }
      }
      toast.error("All 3 pairs are full — remove a player first.");
      return prev;
    });
  };
  const removePlayer = (pairIdx, playerId) => {
    setPairs((prev) => {
      const next = prev.map((p) => [...p]);
      next[pairIdx] = next[pairIdx].filter((id) => id !== playerId);
      return next;
    });
  };

  const autoSort = useMemo(() => {
    const byId = Object.fromEntries((players || []).map((p) => [p.id, p]));
    const total = (ids) => ids.reduce((sum, id) => sum + pts(byId[id]), 0);
    const enriched = pairs.map((ids, idx) => ({ idx, ids, total: total(ids) }));
    enriched.sort((a, b) => a.total - b.total || a.idx - b.idx);
    let tied = null;
    if (enriched[0].total === enriched[1].total && enriched[1].total === enriched[2].total) tied = "all";
    else if (enriched[0].total === enriched[1].total) tied = "seed_1_vs_seed_2";
    else if (enriched[1].total === enriched[2].total) tied = "seed_2_vs_seed_3";
    return {
      sorted: enriched.map((e) => e.ids),
      totals: enriched.map((e) => e.total),
      tied,
    };
  }, [pairs, players]);

  const readyPreview =
    totalSelected === 6 && pairs.every((p) => p.length === 2) ? autoSort : null;

  const validation = useMemo(() => {
    const errs = [];
    if (totalSelected !== 6) errs.push(`Need exactly 6 players (got ${totalSelected}).`);
    if (pairs.some((p) => p.length !== 2)) errs.push("Every pair must have 2 players.");
    if (new Set(pairs.flat()).size !== pairs.flat().length) errs.push("Duplicate player detected.");
    if (readyPreview) {
      for (const pid of readyPreview.sorted[2]) {
        const p = players.find((x) => x.id === pid);
        if (p && ((p.category || p.letter_category || "").toUpperCase() === "A+" || p.is_international)) {
          errs.push(`A+ / international "${p.name}" ends up in Seed 3 — regroup.`);
        }
      }
    }
    return { ok: errs.length === 0, errors: errs };
  }, [pairs, players, readyPreview, totalSelected]);

  const submit = async () => {
    if (!validation.ok) return toast.error(validation.errors[0]);
    if (!readyPreview) return toast.error("Complete all 3 pairs first.");
    setBusy(true);
    try {
      const [s1, s2, s3] = readyPreview.sorted;
      const { data } = await api.post(`/lineups/${tieId}`, {
        seed_1: s1,
        seed_2: s2,
        seed_3: s3,
      });
      toast.success(
        data.revealed
          ? "Both lineups submitted — REVEALED and locked."
          : "Lineup submitted — hidden from opponent until they submit."
      );
      setPayload({
        ...payload,
        my_lineup: {
          seed_1: s1.map((id) => players.find((p) => p.id === id) || { id }),
          seed_2: s2.map((id) => players.find((p) => p.id === id) || { id }),
          seed_3: s3.map((id) => players.find((p) => p.id === id) || { id }),
        },
        my_status: "SUBMITTED",
        revealed: data.revealed,
        locked: data.locked,
      });
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (!payload) return <div className="text-xs text-zinc-500 italic py-4">Loading lineup…</div>;

  const oppName = tie.team_a?.id === team.id ? tie.team_b?.name : tie.team_a?.name;
  const when = tie.matches?.[0]?.scheduled_at
    ? new Date(tie.matches[0].scheduled_at).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "TBD";
  const seedLabels = ["Seed 1", "Seed 2", "Seed 3"];
  const seedColors = ["#F4B400", "#00A86B", "#7DD3FC"];

  return (
    <div data-testid="squad-inline-lineup">
      <div className="mb-3 p-3 rounded-lg border border-[#F4B400]/30 bg-[#F4B400]/5">
        <div className="text-[10px] uppercase tracking-widest text-[#F4B400] font-bold">
          Next Tie · Group into 3 Pairs
        </div>
        <div className="text-sm text-white mt-0.5">
          vs <b>{oppName || "TBD"}</b> · <span className="text-zinc-400">{when}</span>
        </div>
        <div className="text-[10px] text-zinc-500 mt-1">
          {payload.locked ? (
            <span className="text-emerald-300">
              <Lock className="inline w-3 h-3 mr-1" />
              Lineup locked — both teams submitted
            </span>
          ) : payload.my_status === "SUBMITTED" ? (
            <span className="text-amber-300">
              <EyeOff className="inline w-3 h-3 mr-1" />
              Submitted · hidden from opponent
            </span>
          ) : (
            <span>Seeds auto-assigned by pair total (A+=1..E=6). Lowest total = Seed 1.</span>
          )}
        </div>
      </div>

      {pairs.map((pair, pairIdx) => {
        const pairTotal = pair.reduce(
          (sum, id) => sum + pts(players.find((x) => x.id === id)),
          0
        );
        return (
          <div key={pairIdx} className="mb-3" data-testid={`squad-pair-${pairIdx}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-300 font-bold">
                Pair {String.fromCharCode(65 + pairIdx)} · {pair.length}/2
              </div>
              {pair.length === 2 && (
                <div className="text-[10px] uppercase text-[#F4B400] font-bold">
                  Total <span className="text-sm tabular-nums">{pairTotal}</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[0, 1].map((idx) => {
                const pid = pair[idx];
                const p = pid ? players.find((x) => x.id === pid) : null;
                return (
                  <div
                    key={idx}
                    className={`p-2 rounded-lg border min-h-[48px] flex items-center justify-between ${
                      p
                        ? "border-[#F4B400]/30 bg-[#F4B400]/5"
                        : "border-dashed border-white/10 bg-black/20"
                    }`}
                    data-testid={`squad-pair-${pairIdx}-slot-${idx}`}
                  >
                    {p ? (
                      <>
                        <div className="text-xs text-white truncate">
                          {p.name}
                          <span className="text-zinc-500 text-[10px] ml-1">
                            {pts(p)}pt
                          </span>
                        </div>
                        {!payload.locked && (
                          <button
                            onClick={() => removePlayer(pairIdx, pid)}
                            className="text-zinc-500 hover:text-red-400"
                            data-testid={`squad-pair-${pairIdx}-remove-${idx}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="text-[10px] text-zinc-600 italic">empty</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Player picker */}
      {!payload.locked && totalSelected < 6 && (
        <div
          className="mt-2 p-2 rounded-lg border border-white/10 bg-black/40 max-h-[180px] overflow-y-auto"
          data-testid="squad-player-picker"
        >
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 px-1">
            Add players ({6 - totalSelected} remaining)
          </div>
          {players
            .filter((p) => !selected.has(p.id))
            .map((p) => (
              <button
                key={p.id}
                onClick={() => addPlayer(p.id)}
                className="w-full flex items-center justify-between px-2 py-1.5 text-left hover:bg-white/5 rounded"
                data-testid={`squad-add-${p.id}`}
              >
                <span className="text-xs text-white">
                  {p.name}
                  <span className="text-[10px] text-zinc-500 ml-1">
                    Cat {p.category || p.letter_category || "-"} · {pts(p)}pt
                  </span>
                </span>
                <Plus className="w-3.5 h-3.5 text-zinc-400" />
              </button>
            ))}
          {players.filter((p) => !selected.has(p.id)).length === 0 && (
            <div className="text-[10px] text-zinc-500 italic px-2 py-2">
              No eligible players left.
            </div>
          )}
        </div>
      )}

      {/* Auto-seed preview */}
      {readyPreview && (
        <div
          className="mt-3 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5"
          data-testid="squad-auto-seed-preview"
        >
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-2">
            Auto-Seeded Result
          </div>
          {readyPreview.sorted.map((ids, i) => (
            <div
              key={i}
              className="flex items-center gap-2 mb-1"
              data-testid={`squad-seed-preview-${i + 1}`}
            >
              <div
                className="w-14 text-[10px] uppercase tracking-widest font-bold"
                style={{ color: seedColors[i] }}
              >
                {seedLabels[i]}
              </div>
              <div className="flex-1 text-xs text-white truncate">
                {ids
                  .map((id) => (players.find((p) => p.id === id) || {}).name || "?")
                  .join(" + ")}
              </div>
              <div className="text-[10px] text-[#F4B400] font-bold tabular-nums">
                {readyPreview.totals[i]}
              </div>
            </div>
          ))}
          {readyPreview.tied && (
            <div className="mt-2 text-[10px] text-amber-300 italic">
              <AlertTriangle className="inline w-3 h-3 mr-1" />
              Tied pair totals — seed order uses your current pair order.
            </div>
          )}
        </div>
      )}

      {!payload.locked && (
        <>
          <div className="mt-3 space-y-1" data-testid="squad-lineup-validation">
            {validation.errors.length ? (
              validation.errors.map((e, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px] text-red-300">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  {e}
                </div>
              ))
            ) : (
              <div className="flex items-center gap-1.5 text-[11px] text-emerald-300">
                <CheckCircle2 className="w-3 h-3" />
                Ready to submit.
              </div>
            )}
          </div>
          <Button
            onClick={submit}
            disabled={!validation.ok || busy}
            className="mt-3 w-full bg-[#F4B400] text-black font-bold uppercase tracking-widest hover:bg-[#e5aa00] disabled:opacity-40"
            data-testid="squad-lineup-submit"
          >
            {busy ? "Submitting…" : payload.my_status === "SUBMITTED" ? "Update Lineup" : "Submit Lineup"}
          </Button>
        </>
      )}

      <div className="mt-4 pt-3 border-t border-white/5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
          Full Squad ({players.length})
        </div>
        <div className="space-y-1">
          {players.map((p) => (
            <PlayerRow key={p.id} player={p} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PlayerRow({ player }) {
  return (
    <Link to={`/players/${player.id}`} className="flex items-center gap-2 p-1.5 rounded bg-black/20 hover:bg-black/40 transition-colors" data-testid={`player-row-${player.id}`}>
      <div className="w-7 h-7 rounded-full bg-black/40 overflow-hidden shrink-0">
        {player.photo_url && <img src={player.photo_url} alt={player.name} className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-white truncate hover:text-[#F4B400] transition-colors">{player.name}</div>
        <div className="text-[10px] text-zinc-500">
          Cat {player.category || "?"}{player.is_international ? " · INTL" : ""}
          {player.current_ranking ? ` · Rank ${player.current_ranking}` : ""}
        </div>
      </div>
    </Link>
  );
}

function TieRow({ tie, teamId, onOpen }) {
  const opp = tie.opponent || {};
  const myWin = tie.status === "finished" && tie.my_games > tie.opponent_games;
  const myLoss = tie.status === "finished" && tie.my_games < tie.opponent_games;
  return (
    <button
      onClick={onOpen}
      className="w-full text-left p-2.5 rounded-lg border border-white/10 bg-black/20 hover:border-[#F4B400]/40 hover:bg-black/30 transition-colors flex items-center gap-2"
      data-testid={`tie-row-${tie.tie_id}`}
    >
      <div className="w-7 h-7 rounded bg-black/40 overflow-hidden shrink-0">
        {opp.logo_url && <img src={opp.logo_url} alt={opp.name} className="w-full h-full object-contain" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-white truncate">vs {opp.name || "TBD"}</div>
        <div className="text-[10px] text-zinc-500">{STAGE_LABEL[tie.stage] || tie.stage} · {fmtDate(tie.scheduled_at)}</div>
      </div>
      <div className={`text-[10px] uppercase px-2 py-0.5 rounded border ${STATUS_COLORS[tie.status] || STATUS_COLORS.scheduled}`}>{tie.status}</div>
      <div className={`font-anton text-lg ${myWin ? "text-emerald-400" : myLoss ? "text-red-400" : "text-white"}`}>
        {tie.my_games}<span className="text-zinc-500 mx-1">–</span>{tie.opponent_games}
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-500" />
    </button>
  );
}

function TieDetailCard({ tie, teamId, expanded, onToggle }) {
  const opp = tie.opponent || {};
  const myWin = tie.status === "finished" && tie.my_games > tie.opponent_games;
  const myLoss = tie.status === "finished" && tie.my_games < tie.opponent_games;
  const matches = [...(tie.matches || [])].sort((a, b) => (a.seed || 0) - (b.seed || 0));
  return (
    <div className="rounded-xl border border-white/10 bg-[#0a1611] overflow-hidden" data-testid={`tie-detail-${tie.tie_id}`}>
      <button
        onClick={onToggle}
        className="w-full text-left p-3 hover:bg-black/30 transition-colors flex items-center gap-3"
        data-testid={`tie-detail-toggle-${tie.tie_id}`}
      >
        <div className="w-8 h-8 rounded bg-black/40 overflow-hidden shrink-0">
          {opp.logo_url && <img src={opp.logo_url} alt={opp.name} className="w-full h-full object-contain" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-white truncate">
              vs <Link to={`/teams/${opp.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#F4B400] underline decoration-dotted" data-testid={`tie-opp-link-${tie.tie_id}`}>
                {opp.name || "TBD"}
              </Link>
            </span>
            <span className="text-[10px] uppercase text-zinc-500">{STAGE_LABEL[tie.stage] || tie.stage}</span>
            <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${STATUS_COLORS[tie.status] || STATUS_COLORS.scheduled}`}>{tie.status}</span>
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{fmtDate(tie.scheduled_at)}{tie.court ? ` · ${tie.court}` : ""}</div>
        </div>
        <div className={`font-anton text-2xl ${myWin ? "text-emerald-400" : myLoss ? "text-red-400" : "text-white"}`}>
          {tie.my_games}<span className="text-zinc-500 mx-1">–</span>{tie.opponent_games}
        </div>
        <div className="text-[10px] text-zinc-500 min-w-[46px] text-right">
          GD {tie.game_difference > 0 ? "+" : ""}{tie.game_difference}
        </div>
        <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="border-t border-white/10 p-3 space-y-2 bg-black/20" data-testid={`tie-detail-body-${tie.tie_id}`}>
          {matches.map((m) => <SeedMatchRow key={m.id} m={m} isHome={tie.is_home} />)}
        </div>
      )}
    </div>
  );
}

function SeedMatchRow({ m, isHome }) {
  const isSetBased = m.format_type === "set_based";
  const fc = m.format_config || {};
  const sets = m.set_scores || [];
  const gA = m.total_games_won_a ?? m.score_a ?? 0;
  const gB = m.total_games_won_b ?? m.score_b ?? 0;
  const seedLabel = m.seed ? `Seed ${m.seed}` : "Match";
  return (
    <Link to={`/matches/${m.id}`} className="block rounded-lg bg-black/30 border border-white/5 p-2.5 hover:border-[#F4B400]/40 transition-colors" data-testid={`seed-match-${m.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase font-bold text-[#F4B400] tracking-widest">{seedLabel}</span>
        <span className="text-[10px] text-zinc-500">
          {isSetBased ? `${fc.sets_per_match || sets.length}×${fc.games_per_set || 6}${fc.tiebreak ? " · tiebreak" : ""}` : "Time-Based"}
        </span>
        <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${STATUS_COLORS[m.status] || STATUS_COLORS.scheduled} ml-auto`}>{m.status}</span>
        {m.court && <span className="text-[10px] text-zinc-500">· {m.court}</span>}
      </div>
      {isSetBased && sets.length > 0 ? (
        <div className="mt-2 flex items-center gap-3">
          <div className="flex-1 text-xs text-zinc-300">Sets:</div>
          <div className="flex gap-1 flex-wrap">
            {sets.map((s, i) => (
              <div key={i} className="text-center px-2 py-0.5 rounded bg-black/40 border border-white/10 min-w-[46px]" data-testid={`seed-${m.id}-set-${i + 1}`}>
                <div className="text-[8px] text-zinc-500 uppercase">Set {i + 1}</div>
                <div className="font-mono text-sm text-white">{s.a ?? 0}–{s.b ?? 0}</div>
              </div>
            ))}
          </div>
          <div className="text-right ml-2">
            <div className="text-[9px] uppercase text-zinc-500">Games</div>
            <div className="font-anton text-lg text-white">{gA}<span className="text-zinc-500 mx-0.5">–</span>{gB}</div>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex items-center justify-between">
          <div className="text-[10px] text-zinc-500 uppercase">Score</div>
          <div className="font-anton text-lg text-white">{gA}<span className="text-zinc-500 mx-1">–</span>{gB}</div>
        </div>
      )}
    </Link>
  );
}
